import React, { useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import api from '../../lib/api';
import { Sparkles, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * One-click WhatsApp onboarding via Meta Embedded Signup (Facebook Login for
 * Business). The vendor sees a single "Connect WhatsApp" button. Click →
 * Facebook popup → pick business → pick number → approve → done.
 *
 * The vendor never sees phone_number_id / waba_id / access_token.
 *
 * This component self-hides if Embedded Signup is not enabled on the server
 * (META_EMBEDDED_SIGNUP_ENABLED=false or no META_LOGIN_CONFIG_ID set yet).
 * The legacy paste-credentials wizard (only meaningful to admins) is shown
 * via the `fallback` prop when Embedded Signup is unavailable.
 *
 * Exposes an imperative ref handle:
 *   - launch() — programmatically open the FB.login popup (used by per-card
 *     Reconnect buttons so reactivating an existing number also goes through
 *     Embedded Signup).
 *   - isReady() — boolean: SDK loaded + config delivered.
 */
export const EmbeddedSignupButton = forwardRef(function EmbeddedSignupButton(
  { onConnected, onPreflightFail, fallback = null, disabled = false }, ref,
) {
  const [cfg, setCfg] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [busy, setBusy] = useState(false);
  // Mutable bucket Meta's postMessage writes into. We pair it with the
  // FB.login callback's `code` to call the exchange endpoint exactly once.
  const sessionRef = useMemo(() => ({ current: null }), []);

  // ── 1. Pull server-side config (feature flag + app_id + config_id) ──
  useEffect(() => {
    let alive = true;
    api.get('/whatsapp/embedded-signup/config')
      .then(({ data }) => { if (alive) setCfg(data); })
      .catch(() => { if (alive) setCfg({ enabled: false }); });
    return () => { alive = false; };
  }, []);

  // ── 1b. Run the Meta preflight to catch config_id mistakes BEFORE the
  //        seller is left staring at a hung popup. This call hits Meta's
  //        OAuth dialog and tells us whether the configured config_id is
  //        actually a valid Facebook Login for Business → WhatsApp
  //        Embedded Signup configuration.
  const [preflight, setPreflight] = useState(null);
  useEffect(() => {
    if (!cfg?.enabled) return;
    api.get('/whatsapp/embedded-signup/preflight')
      .then(({ data }) => {
        setPreflight(data);
        if (data && data.ok === false && onPreflightFail) onPreflightFail(data);
      })
      .catch(() => setPreflight({ ok: false, diagnostic: 'Could not contact Meta to verify config.' }));
  }, [cfg]);

  // ── 2. Load Meta JS SDK once config arrives ─────────────
  useEffect(() => {
    if (!cfg?.enabled || !cfg.app_id) return;
    if (window.FB) { setSdkReady(true); return; }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: cfg.app_id,
        cookie: true,
        xfbml: false,
        version: cfg.graph_version || 'v22.0',
      });
      setSdkReady(true);
    };

    const id = 'facebook-jssdk';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.onerror = () => toast.error('Could not load Meta SDK — check your network.');
    document.body.appendChild(s);
  }, [cfg]);

  // ── 3. Listen for Meta's WA_EMBEDDED_SIGNUP postMessage ─
  useEffect(() => {
    if (!cfg?.enabled) return;
    const handler = (event) => {
      // Origin guard — only trust Meta
      if (typeof event.origin !== 'string') return;
      if (!event.origin.endsWith('facebook.com') && !event.origin.endsWith('messenger.com')) return;
      let d = event.data;
      if (typeof d === 'string') { try { d = JSON.parse(d); } catch (_) { return; } }
      if (!d || d.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (d.event === 'FINISH' || (typeof d.event === 'string' && d.event.startsWith('FINISH'))) {
        const { phone_number_id, waba_id, business_id } = d.data || {};
        sessionRef.current = { phone_number_id, waba_id, business_id };
      } else if (d.event === 'CANCEL') {
        const step = d.data?.current_step;
        sessionRef.current = { canceled: true, step };
        toast.message(step ? `Signup canceled at: ${step}` : 'Signup canceled');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [cfg, sessionRef]);

  // ── 4. Click handler → FB.login → exchange code ─────────
  const launch = () => {
    if (!sdkReady || !cfg?.enabled || !cfg.config_id) {
      toast.error('Embedded Signup is not ready yet.');
      return;
    }
    // Hard refuse to launch FB.login if Meta says the config_id is invalid —
    // otherwise the SDK will silently hang with no popup. Surface the exact
    // diagnostic from Meta instead.
    if (preflight && preflight.ok === false) {
      toast.error(preflight.diagnostic || 'Meta Embedded Signup configuration is invalid. Check your Meta App Dashboard.', {
        duration: 15000,
      });
      return;
    }
    if (!window.FB || typeof window.FB.login !== 'function') {
      toast.error('Meta SDK not loaded. Refresh and try again.');
      return;
    }
    // ── Safety net: setBusy(false) MUST run on every escape path. We use
    //    `done` + multiple unwinders (FB callback, timeout, focus-back) so
    //    "Connecting..." can never get stuck forever even when the FB popup
    //    is blocked / swallowed / closed without action.
    setBusy(true);
    sessionRef.current = null;
    let done = false;
    const finish = (reason) => {
      if (done) return;
      done = true;
      setBusy(false);
      // Detach the focus-back fallback
      try { window.removeEventListener('focus', onFocusBack); } catch (_) { /* noop */ }
      try { clearTimeout(timeoutId); } catch (_) { /* noop */ }
      console.info('[FABTEXS Embedded Signup] finish:', reason);
    };

    // (a) Hard 60-second wall-clock timeout — covers "popup never opened"
    //     and "user walked away with popup still open".
    const timeoutId = setTimeout(() => {
      if (done) return;
      toast.error('WhatsApp connect timed out. Please retry — make sure popups are allowed.');
      finish('timeout-60s');
    }, 60000);

    // (b) Focus-back fallback — when the FB popup closes, the parent regains
    //     focus. Wait 2.5s for the FB callback / postMessage to land; if
    //     nothing arrived, treat as cancellation and unfreeze the UI.
    const onFocusBack = () => {
      setTimeout(() => {
        if (done) return;
        if (!sessionRef.current?.phone_number_id) {
          toast.message('Connect WhatsApp was cancelled. Click again to retry.');
          finish('focus-back-no-session');
        }
      }, 2500);
    };
    window.addEventListener('focus', onFocusBack);

    // (c) FB.login itself — wrapped in try/catch in case the SDK throws
    //     synchronously (which it does when config_id is malformed / app is
    //     in dev mode and the user isn't a tester).
    try {
      console.info('[FABTEXS Embedded Signup] calling FB.login', { config_id: cfg.config_id });
      window.FB.login(
        function (response) {
          (async () => {
            try {
              console.info('[FABTEXS Embedded Signup] FB.login callback', response);
              const code = response?.authResponse?.code;
              if (!code) {
                // status could be 'unknown' (popup closed), 'not_authorized',
                // or anything else — give a useful message in each case.
                const status = response?.status || 'no_code';
                toast.error(`Meta did not return a signup code (status: ${status}). Please retry.`);
                return;
              }
              // Brief grace for the postMessage to land before we POST.
              for (let i = 0; i < 30 && !sessionRef.current; i++) {
                await new Promise((r) => setTimeout(r, 100));
              }
              if (sessionRef.current?.canceled) return; // already toasted
              const sess = sessionRef.current || {};
              if (!sess.phone_number_id || !sess.waba_id) {
                toast.error('Meta did not return the WABA/phone IDs — please retry the connect flow.');
                return;
              }
              const startedAt = Date.now();
              const { data } = await api.post('/whatsapp/embedded-signup/exchange', {
                code,
                phone_number_id: sess.phone_number_id,
                waba_id: sess.waba_id,
                business_id: sess.business_id || '',
              });
              const elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
              const warn = data?.activation?.warning;
              if (warn) toast.warning(`Connected in ${elapsed}s, but: ${warn}`);
              else toast.success(`WhatsApp connected in ${elapsed}s ✓ — broadcasts are live.`);
              onConnected && onConnected(data);
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message || 'Embedded signup failed');
              console.error('[FABTEXS Embedded Signup] exchange failed', e);
            } finally {
              finish('fb-callback');
            }
          })();
        },
        {
          config_id: cfg.config_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            sessionInfoVersion: '3',
            feature: 'whatsapp_embedded_signup',
          },
        }
      );
    } catch (err) {
      console.error('[FABTEXS Embedded Signup] FB.login threw synchronously', err);
      toast.error(`Meta SDK error: ${err?.message || err}`);
      finish('fb-sync-throw');
    }
  };

  // Expose imperative handle so parent components (per-number Reconnect cards)
  // can launch FB.login programmatically without rendering a second button.
  useImperativeHandle(ref, () => ({
    launch,
    isReady: () => Boolean(sdkReady && cfg?.enabled && cfg?.config_id),
  }), [sdkReady, cfg]);

  // Render-time gating
  if (cfg === null) {
    return (
      <button disabled className="btn-wa text-[13px] px-4 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 opacity-50"
        data-testid="wa-embedded-signup-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </button>
    );
  }
  if (!cfg.enabled) return fallback;

  return (
    <button
      data-testid="wa-embedded-signup-btn"
      onClick={() => {
        // Surface a clear reason when the button is gated by parent (plan
        // limit) or SDK still warming up. Silent disabled buttons confuse
        // vendors — the previous behaviour was "click did nothing".
        if (disabled) {
          toast.message('Plan limit reached — disconnect an existing number or upgrade to add another.');
          return;
        }
        if (!sdkReady) {
          toast.message('Meta SDK is still loading — please wait a second and try again.');
          return;
        }
        launch();
      }}
      disabled={busy}
      aria-disabled={disabled || !sdkReady || busy}
      className={`btn-wa text-[13px] px-4 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 ${(disabled || busy || !sdkReady) ? 'opacity-60 cursor-not-allowed' : ''}`}
      title={disabled ? 'Plan limit reached' : 'One-click WhatsApp activation via Facebook'}
    >
      {busy
        ? (<><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>)
        : !sdkReady
          ? (<><MessageCircle className="w-4 h-4" /> Preparing Meta SDK…</>)
          : (<><Sparkles className="w-4 h-4" /> Connect WhatsApp</>)}
    </button>
  );
});

export default EmbeddedSignupButton;
